import { isNumber } from "lodash";
import Gantt from "PFApp/components/gantt/gantt";
import { ProfileAvatarConnected } from "PFApp/components/profile_avatar_connected";
import { useRoleJobCode } from "PFApp/use_role_job_code";
import Tooltip from "PFComponents/tooltip/tooltip";
import { profileResponseShapePropType } from "PFCore/base/prop_types/profile_response_prop_types";
import { fetchPaginatedCollection } from "PFCore/helpers/collection";
import { getProfileName } from "PFCore/helpers/profile";
import { useDateFormatter } from "PFCore/hooks/use_date_formatter";
import { ApiRoute } from "PFCore/utilities/routes";
import WarningIcon from "PFIcons/warning.svg";
import PropTypes from "prop-types";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import css from "./audit_planner_gantt.module.scss";

const ExceptionTooltipContent = ({ exception, profile }) => {
  const { formatDate } = useDateFormatter();
  const { t } = useTranslation("auditPlanner");

  return (
    <div className={css.tooltip}>
      <strong style={{ display: "block" }}>{t("parts.overbookingDetected")}</strong>
      <br />
      {(exception.exceptions?.overbooking || []).map((text) => (
        <div key={text} className={css.tooltipErrorMessage}>
          {text}
        </div>
      ))}
      {(exception.exceptions?.isqm_overbooking || []).map(({ weeks, start_date }, index) => (
        <div key={index} className={css.tooltipErrorMessage}>
          {t("parts.overbookingDetails", {
            name: getProfileName(profile),
            weeks,
            date: formatDate(start_date)
          })}
        </div>
      ))}
      <br />
    </div>
  );
};

const RowItem = ({ profileData }) => {
  const { t } = useTranslation("auditPlanner");
  const { profile, optional_fields, exception } = profileData;
  const isIsqm = !!exception?.exceptions?.isqm_overbooking;
  const hoursAvailable =
    !isNaN(parseInt(optional_fields.available_minutes)) &&
    Math.round(parseInt(optional_fields.available_minutes) / 60);

  return (
    <div className={css.label}>
      <div>
        <ProfileAvatarConnected profile={profile} size={40} smartLink />
      </div>
      <div className={css.details}>
        <div className={css.name}>
          {exception && (
            <Tooltip content={<ExceptionTooltipContent exception={exception} profile={profile} />}>
              <span>
                <WarningIcon
                  height={20}
                  width={20}
                  style={{ fill: isIsqm ? "#111" : "var(--Palette-error)" }}
                />
              </span>
            </Tooltip>
          )}
          <Tooltip content={<div className={css.tooltip}>{getProfileName(profile)}</div>}>
            <span>{getProfileName(profile)}</span>
          </Tooltip>
        </div>

        {isNumber(hoursAvailable) && <div>{t("parts.hoursAvailableCount", { count: hoursAvailable })} </div>}
      </div>
    </div>
  );
};
const InfoPanel = ({ data }) => {
  const { t } = useTranslation("auditPlanner");
  const totalHoursAvailable = useMemo(() => {
    if (!data || isNaN(data.meta.total_available_minutes)) {
      return false;
    }
    /* sum items to avoid issue when rounded total is different than sum of rounded items */
    if (data.entries.length === data.meta.total) {
      return data.entries.reduce(
        (sum, item) =>
          sum +
          (isNaN(item.optional_fields.available_minutes)
            ? 0
            : Math.round(parseInt(item.optional_fields.available_minutes) / 60)),
        0
      );
    } else {
      return Math.round(parseInt(data.meta.total_available_minutes) / 60);
    }
  }, [data]);

  return (
    <div className={css.infoPanel}>
      {totalHoursAvailable && (
        <div>{t("parts.totalHoursAvailableCount", { count: totalHoursAvailable })}</div>
      )}
    </div>
  );
};

const AuditPlannerGantt = ({ data, monthsLimit }) => {
  const jobCodeDisplayAs = useRoleJobCode();
  const { formatISODate } = useDateFormatter();

  const [exceptions, setExceptions] = useState([]);

  const entries = data.entries || [];
  const profileIds = entries.map((entry) => entry.id);

  useEffect(() => {
    fetchPaginatedCollection(ApiRoute("/api/exception_requests"), {
      profile_ids: profileIds,
      state: ["pending", "approved"],
      exception_type: ["availability"]
    }).then((collection) => setExceptions(collection.entries));
  }, profileIds);

  const rowItems = useMemo(
    () =>
      entries.map((entry) => {
        const exception = exceptions.find(({ profile_id }) => profile_id === entry.id);
        return { ...entry, exception };
      }),

    [exceptions, entries]
  );

  const availabilityStart = data.meta.filters.availability?.value?.start;
  const startDate = useMemo(() => {
    const date = moment
      .utc(availabilityStart || undefined)
      .startOf("isoWeek")
      .startOf("day");

    date.startOf("isoWeek").subtract(7, "days");
    return formatISODate(date);
  }, [availabilityStart]);

  const getClashesFromRowItem = (rowItem) =>
    rowItem.exception?.exceptions?.isqm_overbooking?.map(({ weeks, start_date }, isqmIndex) => {
      const start = moment(start_date).endOf("day");
      const end = moment(start_date).add(weeks, "weeks").startOf("day");
      const id = `overbooking_clash_${rowItem.id}_${isqmIndex}`;
      return {
        start,
        end,
        id,
        key: id,
        title: "isqm overbooking",
        className: css.isqmClash
      };
    }) || [];

  return (
    <Gantt
      style={{ margin: "0 0 30px 0" }}
      monthsLimit={monthsLimit}
      rowItems={rowItems}
      startDate={startDate}
      changeStartDate={() => {}}
      availabilityInfo={{}}
      infoPanel={<InfoPanel data={data} />}
      rowItemLabelComponent={(profileData) => <RowItem profileData={profileData} />}
      showToday
      showClashes
      getClashesFromRowItem={getClashesFromRowItem}
      jobCodeDisplayAs={jobCodeDisplayAs}
    />
  );
};
ExceptionTooltipContent.propTypes = {
  exception: PropTypes.shape({
    exceptions: PropTypes.object
  }),
  profile: PropTypes.shape({
    first_name: PropTypes.string,
    last_name: PropTypes.string
  })
};

AuditPlannerGantt.displayName = "AuditPlannerGantt";
AuditPlannerGantt.propTypes = {
  data: PropTypes.shape({
    entries: PropTypes.array.isRequired,
    meta: PropTypes.object.isRequired
  }).isRequired,
  monthsLimit: PropTypes.number.isRequired
};

InfoPanel.propTypes = {
  data: PropTypes.shape({
    entries: PropTypes.array,
    meta: PropTypes.shape({
      total_available_minutes: PropTypes.number,
      total: PropTypes.number
    })
  })
};

RowItem.propTypes = {
  profileData: profileResponseShapePropType.isRequired
};

export default AuditPlannerGantt;
